import { Check, ExternalLink } from 'lucide-react';

import type { CampaignAnalysisTopPostExample } from '@/entities/campaign/generated/campaign-type';
import {
  formatCompactNumber,
  formatPercent,
  getCreatorProfileParts,
  getCreatorProfileUrl,
  getTopVideoPlatform,
} from '../../helpers/analysis-report';

export function TopVideosSection({
  videos,
}: {
  videos: readonly CampaignAnalysisTopPostExample[];
}) {
  return (
    <div className="flex flex-col gap-4">
      {videos.map((video) => {
        const platform = getTopVideoPlatform(video.post_source_url);
        const PlatformLogo = platform.Logo;
        const creatorProfileUrl = getCreatorProfileUrl(platform, video.creator_username);
        const creatorProfileParts = getCreatorProfileParts(video.creator_profile);
        const whyItWorkedItems = [video.why_it_worked];

        return (
          <div
            key={video.rank}
            data-campaign-analysis-print-avoid-break
            className="border-border bg-card flex flex-col gap-6 rounded-xl border p-5 shadow-sm sm:p-6 print:gap-4 print:p-4"
          >
            <div
              data-campaign-analysis-top-video-header
              className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_auto]"
            >
              <div className="grid min-w-0 grid-cols-[auto_minmax(0,1fr)] gap-4 sm:gap-6 print:gap-4">
                <div className="text-foreground text-base leading-6 font-bold tabular-nums sm:text-lg">
                  {video.rank}
                </div>
                <div className="flex max-w-160 min-w-0 flex-col gap-2 print:max-w-none">
                  <a
                    href={video.post_source_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-foreground inline-flex max-w-full items-center gap-3 text-base leading-6 font-bold underline-offset-4 hover:underline sm:text-lg print:text-base"
                  >
                    <span className="min-w-0 break-words">{video.title}</span>
                    <ExternalLink className="size-4 shrink-0" />
                  </a>
                  <p className="text-muted-foreground text-sm leading-5">
                    {video.content_description}
                  </p>
                </div>
              </div>
              <div
                data-campaign-analysis-top-video-stats
                className="flex flex-wrap gap-x-6 gap-y-3 pl-10 lg:justify-end lg:pl-0 print:gap-x-5"
              >
                <div className="min-w-16">
                  <p className="text-muted-foreground text-sm leading-5 font-medium">Views</p>
                  <p className="text-foreground mt-1 text-xl leading-7 font-bold tracking-tight tabular-nums sm:text-2xl print:text-xl">
                    {formatCompactNumber(video.views)}
                  </p>
                </div>
                {video.engagement_rate === null ? null : (
                  <div className="min-w-20">
                    <p className="text-muted-foreground text-sm leading-5 font-medium">ER</p>
                    <p className="text-foreground mt-1 text-xl leading-7 font-bold tracking-tight tabular-nums sm:text-2xl print:text-xl">
                      {formatPercent(video.engagement_rate)}
                    </p>
                  </div>
                )}
              </div>
            </div>
            <div className="grid gap-4 pl-0 sm:pl-8.5 md:grid-cols-[96px_minmax(0,1fr)] lg:grid-cols-[112px_minmax(0,1fr)] print:grid-cols-[72px_minmax(0,1fr)] print:gap-3 print:pl-7">
              <a
                href={video.post_source_url}
                target="_blank"
                rel="noreferrer"
                className="border-border bg-muted relative block aspect-[9/16] w-24 overflow-hidden rounded-xl border lg:w-28 print:w-18 print:rounded-lg"
                aria-label={`Open ${video.title} on ${platform.label}`}
              >
                <img
                  src={video.thumbnail_url}
                  alt={`${video.title} post thumbnail`}
                  className="size-full object-cover"
                  loading="lazy"
                />
              </a>
              <div className="flex min-w-0 flex-col gap-4 print:gap-3">
                <div className="flex flex-col gap-2">
                  <a
                    href={creatorProfileUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-foreground inline-flex w-fit min-w-0 items-center gap-2 text-base leading-none font-semibold underline-offset-4 hover:underline"
                  >
                    <span>@{video.creator_username}</span>
                    <PlatformLogo aria-hidden="true" className="size-3.5 shrink-0" />
                  </a>
                  {creatorProfileParts.length > 0 ? (
                    <p
                      className="text-foreground flex flex-wrap items-center gap-x-2.5 gap-y-1 text-sm leading-5"
                      data-campaign-analysis-creator-profile
                    >
                      {creatorProfileParts.map((part, index) => (
                        <span key={part} className="inline-flex items-center gap-2.5">
                          {index > 0 ? <span className="text-muted-foreground">/</span> : null}
                          {part}
                        </span>
                      ))}
                    </p>
                  ) : null}
                </div>
                <div className="flex flex-col gap-3">
                  <p className="text-muted-foreground text-sm leading-5 font-semibold">
                    Why it worked
                  </p>
                  <ul className="text-foreground flex list-none flex-col gap-2 text-sm leading-5 print:gap-1">
                    {whyItWorkedItems.map((reason) => (
                      <li key={reason} className="grid grid-cols-[auto_minmax(0,1fr)] gap-2.5">
                        <Check
                          aria-hidden="true"
                          className="text-muted-foreground mt-0.5 size-3.5 shrink-0"
                          strokeWidth={2.5}
                        />
                        <span>{reason}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
